import Dropdown from '@tencent/dui/lib/components/Dropdown';
import { isPromise } from '@tencent/workbench-core';
import React, { forwardRef, useContext, useState, useCallback, useImperativeHandle, PureComponent } from 'react';
import { EnvContext } from '../../../controllers/workbench/context.js';
import { useDrag } from '../../../hooks/use-drag/index.js';
import { BuiltinUiState } from '../../../services/store/builtin-state.js';
import { promiseElementHOC } from '../../../utils/util-hoc/promise-element-hoc.js';
import { ToolbarContext } from '../components/toolbar-context.js';
import { evaluate } from '../../../controllers/base/evaluate.js';
import styles from './toolbar-button.less.js';

class DropdownArrow extends PureComponent {
    render() {
        var _a, _b;
        const wrapperClass = [styles['arrow-container'], (_a = this.props.className) !== null && _a !== void 0 ? _a : ''];
        if (this.props.isFocusArrow) {
            wrapperClass.push(styles['toolbar-item-focus']);
        }
        if (this.props.clickable) {
            wrapperClass.push(styles.clickable);
        }
        const ariaLabel = (_b = this.props.ariaAttributes) === null || _b === void 0 ? void 0 : _b['aria-label'];
        return React.createElement("div", { className: wrapperClass.join(' '), onClick: this.props.clickable ? this.props.onDropdownClick : undefined, role: this.props.clickable ? 'button' : '', "aria-hidden": !this.props.clickable, "aria-expanded": this.props.clickable && this.props.active, "aria-label": this.props.clickable ? `${ariaLabel} 更多选项` : undefined, onMouseDown: (event) => {
                event.preventDefault();
            } },
            React.createElement("div", { className: styles.arrow }));
    }
}
const ToolbarDropdownButton = forwardRef((props, ref) => {
    const { alignment, name, unionClick, hasArrow, disabled, preventDropdownScrollHide, isFocus, dragOptions, dropdownPlacement, dropdownContent, renderChildren, onButtonClick, onButtonDoubleClick, onVisibleChange, onDropdownClick, shouldDrag, onDrag, onDragStart, onDragEnd, } = props;
    const envContext = useContext(EnvContext);
    const toolbarContext = useContext(ToolbarContext);
    const [visible, setVisible] = useState(false);
    const enableDragging = evaluate(shouldDrag, false);
    const { refCallback, } = useDrag({
        enable: enableDragging,
        onDrag,
        onDragEnd,
        onDragStart,
        dragOptions,
    });
    const handleClick = useCallback((event) => {
        envContext.store.update(BuiltinUiState.ToolbarDropdownEvent, { target: name, type: 'click' });
        onButtonClick(event);
        if (!unionClick && dropdownContent) {
            event.stopPropagation();
        }
        envContext.store.update(BuiltinUiState.isFocusedByClick, true);
    }, [envContext, name, onButtonClick, unionClick, dropdownContent]);
    const handleDoubleClick = useCallback((event) => {
        envContext.store.update(BuiltinUiState.ToolbarDropdownEvent, { target: name, type: 'doubleClick' });
        onButtonDoubleClick(event);
        envContext.store.update(BuiltinUiState.isFocusedByClick, true);
    }, [envContext, name, onButtonDoubleClick]);
    const shouldShowArrow = useCallback(() => {
        if (hasArrow !== undefined)
            return hasArrow;
        return true;
    }, [hasArrow]);
    const handleVisibleChange = useCallback((newVisible) => {
        envContext.store.update(BuiltinUiState.ToolbarDropdownEvent, { target: name, type: newVisible ? 'dropdownOpen' : 'dropdownClose' });
        onVisibleChange === null || onVisibleChange === void 0 ? void 0 : onVisibleChange(newVisible);
        setVisible(newVisible);
    }, [envContext, onVisibleChange]);
    useImperativeHandle(ref, () => ({
        handleHiddenDropdown: () => {
            handleVisibleChange(false);
        },
        handleVisibleChange,
    }), [handleVisibleChange]);
    const getInnerElement = () => {
        const actualButtonProps = {
            onClick: handleClick,
            onDoubleClick: handleDoubleClick,
            onMouseDown: (event) => {
                if (!enableDragging)
                    event.preventDefault();
            },
            arrow: shouldShowArrow()
                ? React.createElement(DropdownArrow, Object.assign({ clickable: !unionClick, active: visible }, props, { onDropdownClick: (e) => {
                        handleVisibleChange(!visible);
                        onDropdownClick(e);
                    } }))
                : undefined,
            style: enableDragging
                ? {
                    cursor: 'grab',
                }
                : undefined,
        };
        return renderChildren(props, actualButtonProps, refCallback);
    };
    /** 获取当前编辑器的对焦回调 */
    const requestFocus = useCallback(() => { var _a, _b; 
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    return (_b = (_a = envContext.editor) === null || _a === void 0 ? void 0 : _a.surface) === null || _b === void 0 ? void 0 : _b.requestFocus; }, [envContext]);
    const element = getInnerElement();
    if (dropdownContent) {
        let content = null;
        if (visible) {
            const dropdownContentValue = dropdownContent();
            content = isPromise(dropdownContentValue)
                ? promiseElementHOC(dropdownContentValue)
                : dropdownContentValue;
        }
        const dropdownProps = {
            dropContent: content,
            disabled,
            dropContentStyle: {
                marginLeft: 0,
                marginTop: 0,
            },
            alignment,
            placement: dropdownPlacement,
            key: 'inner-dropdown',
            visible,
            onVisibleChange: handleVisibleChange,
            autoFocus: true,
            viewportMargin: 4,
            className: styles['dropdown-button'],
            disableScrollHide: preventDropdownScrollHide,
            zIndex: 9998,
            onRestoreFocus: () => {
                if (!requestFocus() || isFocus) {
                    // 无法获取编辑器对焦，或者当前是属于无障碍键盘操作
                    // 则直接返回，让 dui 组件走默认对焦能力
                    return;
                }
                // 根据上下文找到此时对应编辑器，在关闭弹窗时自动对焦到此编辑器上，避免走 dui 默认对焦逻辑
                // 防止 dui 在非无障碍操作情况下存储 activeElement 时，捕获到了 toolbar，导致 dui 默认对焦到 toolbar 触发 toolbar 的无障碍能力
                requestFocus();
                // 返回 false 阻止 dui 组件默认对焦能力
                return false;
            },
        };
        return (React.createElement(Dropdown, Object.assign({}, dropdownProps, { ref: node => { var _a; return (_a = toolbarContext.dropdownRefs) === null || _a === void 0 ? void 0 : _a.set(name, node); } }), element));
    }
    return element;
});

export { ToolbarDropdownButton };
//# sourceMappingURL=dropdown-button.js.map
